import { useLazyQuery, useQuery } from "@apollo/client/react";
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import 'moment/locale/es';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { adaptDelegacionReclamosArray } from '../../../adapters/delegacionAdapter';
import { adaptTipoProfesionalArray } from '../../../adapters/tipoProfesionalAdapter';
import { ButtonOptionGroup } from '../../../components/buttonOptionGroup/ButtonOptionGroup';
import { CustomButton } from '../../../components/customButton/CustomButton';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import { PopoverModal } from '../../../components/popoverModal/PopoverModal';
import { GET_DELEGACIONES_BY_USUARIO } from '../../../data/graphQL/queries/PacienteProbable/getAllDelegacionesByUsuario';
import { GET_ALL_TIPO_PROFESIONALES } from '../../../data/graphQL/queries/PlanCuidado/getTipoProfesionales';
import adaptTramosArray, { GET_TRAMOS } from '../../../data/graphQL/queries/Tramos/getTramos';
import { GET_USUARIO_DELEGACION_ORIGEN } from '../../../data/graphQL/queries/usuarios/getUsuarioDelegacionOrigen';
import { usePersistFilters } from '../../../hooks/usePersistFilters';
import { handleClientTextFilter } from './helpers/handleClientTextFilter';

const storageId = sessionStorage.getItem('idUsuario');

const ESTADO_AC = 'Activos';
const ESTADO_IN = 'Inactivos';
const ESTADO_ALL = 'Todos';
const estadosOptions = [ESTADO_AC, ESTADO_IN, ESTADO_ALL];

const TIPO_PROF_TODOS = { id: -1, displayName: 'Todos' };

const idUsuarioLoggeado = sessionStorage.getItem('idUsuario');


export const TramosContent = ({ setChildrenAction }) => {
  const navigate = useNavigate();

  const [delegacionOrigen, setDelegacionOrigen] = useState(0);
  const [delegaciones, setDelegaciones] = useState([]);
  const [tipoProfesionales, setTipoProfesionales] = useState([]);

  const [allowedWrite, setAllowedWrite] = useState(false);

  const {
    tramos,
    tramosLoading,
    totalCount,
    filters,
    handleChangeFilters,
    handleTextFilter,
    handleTextClean
  } = useGetTramos(delegacionOrigen);

  const { data: tipoProfesionalesData } = useQuery(GET_ALL_TIPO_PROFESIONALES);

  useEffect(() => {
    if (!tipoProfesionalesData) return;
    setTipoProfesionales(adaptTipoProfesionalArray(tipoProfesionalesData));
  }, [tipoProfesionalesData]);

  const initialColumns = [
    {
      headerName: '',
      sortable: false,
      field: 'action',
      flex: 40,
      renderCell: (cellProps) => {
        const { row } = cellProps;

        return (
          <PopoverModal>
            <div className="popover-modal-content">
              <div onClick={() => handleClickView(row)} aria-hidden='true'
                className='item-option clickable-option'>

                <VisibilityIcon className='edit-icon' />
                <span className='toggle-action'>Ver</span>
              </div>
              <div onClick={() => handleClickEdit(row)} aria-hidden='true'
                className='item-option clickable-option'>

                <EditIcon className='edit-icon' />
                <span className='toggle-action'>Editar</span>
              </div>
            </div>
          </PopoverModal>);

      }
    },
    {
      flex: 60,
      field: 'id',
      headerName: 'ID'
    },
    {
      flex: 140,
      field: 'idDelegacion',
      headerName: 'Delegación',
      valueGetter: (idDelegacion) => {
        return delegaciones.find((delegacion) => delegacion.id === idDelegacion)?.displayName || '-';
      }
    },
    {
      flex: 200,
      field: 'descripcion',
      headerName: 'Descripción'
    },
    {
      flex: 200,
      field: 'idTipoProfesional',
      headerName: 'Tipo Profesional',
      valueGetter: (idTipoProfesional) => {
        return tipoProfesionales?.find((x) => x.id === idTipoProfesional)?.displayName;
      }
    },
    {
      flex: 110,
      field: 'valor',
      headerName: 'Valor',
      valueGetter: (valor) => {
        return '$' + parseFloat(valor || 0).toLocaleString('es-AR');
      }
    },
    {
      flex: 90,
      field: 'activo',
      headerName: 'Activo',
      renderCell: ({ row }) => {
        const { activo } = row;
        const icon = activo ?
          <CheckIcon className='icon-grid' /> :
          <ClearIcon className='icon-grid' />;
        return <Tooltip title={activo ? 'Activo' : 'Inactivo'}>{icon}</Tooltip>;
      }
    },
    {
      flex: 110,
      field: 'fechaExpira',
      headerName: 'F. Expira',
      type: 'date'
    }];


  const columns = useMemo(() => generateColumnsDefaultConfiguration(initialColumns, true), [delegaciones, tipoProfesionales]);

  const [getDelegaciones, { loading: delegacionesLoading, data: delegacionesData }] = useLazyQuery(GET_DELEGACIONES_BY_USUARIO, {
    fetchPolicy: 'no-cache',
  });

  const [getDelegacionOrigen, { data: delegacionOrigenData }] = useLazyQuery(GET_USUARIO_DELEGACION_ORIGEN, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!delegacionesData) return;
    setDelegaciones(adaptDelegacionReclamosArray(delegacionesData));
  }, [delegacionesData]);

  useEffect(() => {
    if (!delegacionOrigenData) return;
    if (delegacionOrigenData.usuarioFull.success) {
      const deleg = parseInt(delegacionOrigenData.usuarioFull.valorEsperado.delegacionOrigen, 10);
      sessionStorage.setItem('delegacionOrigen', deleg);
      setDelegacionOrigen(deleg);

      if (filters.idDelegacion == 0)
        handleChangeFilters('idDelegacion', deleg);
    }
  }, [delegacionOrigenData]);

  useEffect(() => {
    setChildrenAction(() =>
      <div className='top-buttons'>
        <CustomButton
          className='button-header'
          label='Nuevo'
          isLoading={delegacionesLoading}
          onClick={handleClickCreate}
          variant='contained' />

      </div>
    );
  }, [setChildrenAction, delegacionOrigen, delegacionesLoading, delegaciones, tipoProfesionales]);

  useEffect(() => {
    if (!delegacionesLoading) {
      getDelegaciones({ variables: { idUsuario: JSON.parse(idUsuarioLoggeado) } });
    }
    getDelegacionOrigen({ variables: { idUsuario: JSON.parse(storageId) } });
  }, [idUsuarioLoggeado, storageId]);

  const handleClickCreate = () => {
    navigate(`/honorarios/nuevoTramo`,
      {
        state: {
          idTramo: 0,
          isNew: true,
          allowedWrite: true,
          delegaciones,
          tipoProfesionales
        }
      }
    );
  };

  const handleClickEdit = (row) => {
    navigate(`/honorarios/nuevoTramo`,
      {
        state: {
          idTramo: row.id,
          isNew: false,
          allowedWrite: true,
          delegaciones,
          tipoProfesionales
        }
      }
    );
  };

  const handleClickView = (row) => {
    navigate(`/honorarios/nuevoTramo`,
      {
        state: {
          idTramo: row.id,
          isNew: false,
          allowedWrite: false,
          delegaciones,
          tipoProfesionales
        }
      }
    );
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    handleChangeFilters(name, value);
  };

  return (
    <div className='grid-container requisitos-clientes-grilla'>
      <Grid
        container
        spacing={2}
        sx={{ marginBottom: '25px' }}>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <TextField
            label='Buscar'
            name='txtFiltro'
            size='small'
            type='text'
            fullWidth
            variant='outlined'
            value={filters?.txtFiltro}
            onChange={(e) => handleChangeFilters('txtFiltro', e.target.value)}
            onKeyUp={handleTextFilter}
            sx={{ backgroundColor: 'white' }}
            InputProps={{
              endAdornment:
                <InputAdornment position='end'>
                  <SearchIcon
                    sx={{ cursor: 'pointer' }} />

                  <ClearIcon
                    onClick={handleTextClean}
                    sx={{ cursor: 'pointer' }} />

                </InputAdornment>

            }} />

        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-delegacion'>Delegación</InputLabel>
            <Select
              labelId='select-delegacion'
              options={delegaciones}
              label='Delegación'
              onChange={handleChange}
              value={filters?.idDelegacion || undefined}
              name='idDelegacion'>

              {delegaciones && delegaciones.map((delegacion) => {
                return <MenuItem key={delegacion.id} value={delegacion.id}> {delegacion.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-area-responsable'>Tipo profesional</InputLabel>
            <Select
              labelId='select-area-responsable'
              options={tipoProfesionales}
              label='Tipo profesional'
              onChange={handleChange}
              value={filters?.tipoProfesional || undefined}
              name='tipoProfesional'>

              <MenuItem key={TIPO_PROF_TODOS.id} value={TIPO_PROF_TODOS.id}> {TIPO_PROF_TODOS.displayName} </MenuItem>

              {tipoProfesionales && tipoProfesionales.map((tipo) => {
                return <MenuItem key={tipo.id} value={tipo.id}> {tipo.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly', flexWrap: 'wrap' }}>
            <ButtonOptionGroup
              key={filters?.estado}
              options={estadosOptions}
              onOptionClick={(v) => handleChange({ target: { name: 'estado', value: v } })}
              selectedOption={filters?.estado} />

          </Box>
        </Grid>
      </Grid>
      <DataGridGenerico
        columns={columns}
        totalCount={totalCount}
        rows={tramos}
        toolbar={false}
        loading={tramosLoading} />

    </div>);

};

const useGetTramos = (delegacionOrigen) => {
  const [tramos, setTramos] = useState([]);
  const [datosOriginal, setDatosOriginal] = useState([]);

  const { filters, handleChangeFilters } = usePersistFilters({
    idDelegacion: delegacionOrigen,
    estado: 0,
    tipoProfesional: -1,
    txtFiltro: ''
  }, `TramosFilter`);

  const [getTramos, { tramosLoading, data: dataTramos, error: errorTramos }] = useLazyQuery(GET_TRAMOS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!dataTramos) return;
    const adaptedData = adaptTramosArray(dataTramos ?? []);
    setTramos(handleClientTextFilter(filters?.txtFiltro, adaptedData));
    setDatosOriginal(adaptedData);
  }, [dataTramos]);

  useEffect(() => {
    if (errorTramos) {
      toast.error('No se pudo cargar la tabla de tramos, intentelo nuevamente más tarde.');
    }
  }, [errorTramos]);

  useEffect(() => {
    if (tramosLoading) return;
    getTramos({
      variables: {
        where: {
          and: [
            filters.idDelegacion !== -1 ?
              { idDelegacion: { eq: filters.idDelegacion } } :
              {},
            filters.tipoProfesional !== -1 ?
              { idTipoProfesional: { eq: filters.tipoProfesional } } :
              {},
            filters.estado !== 2 ?
              { activo: { eq: !filters.estado } } :
              {}]

        }
      }
    });
  }, [
    filters.idDelegacion,
    filters.tipoProfesional,
    filters.estado
  ]);

  const handleTextClean = () => {
    handleChangeFilters('txtFiltro', '');
    setTramos(datosOriginal);
  };

  const handleTextFilter = () => {
    const inputBuscar = filters?.txtFiltro;

    setTramos(handleClientTextFilter(inputBuscar, datosOriginal));
  };

  return {
    tramos: tramos,
    tramosLoading: false,
    totalCount: tramos?.allTramos?.length || 0,
    filters,
    handleChangeFilters,
    handleTextFilter,
    handleTextClean
  };
};